/**
 * Sidebar Component — Left navigation for the application.
 *
 * Provides project navigation, user profile, and collapse toggle.
 * Assets section shows live folder tree from FileManagerContext.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Home, FolderOpen, Scissors, LogOut, User, ChevronLeft, ChevronRight, Settings, Cpu, Activity } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '../../contexts/auth'

interface SidebarProps {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
}

interface SubNavItem {
  path: string
  label: string
  icon: LucideIcon
}

interface NavItem {
  path: string
  label: string
  icon: LucideIcon
  adminOnly?: boolean
  disabled?: boolean
  subtitle?: string
  children?: SubNavItem[]
}

const Sidebar = ({ isCollapsed, setIsCollapsed }: SidebarProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const navItems: NavItem[] = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/projects', label: 'Assets', icon: FolderOpen },
    { path: '/analysis', label: 'Analysis', icon: Cpu },
    { path: '/assemblies', label: 'Assemblies', icon: Scissors },
    { path: '/system', label: 'System', icon: Settings },
    { path: '/monitoring', label: 'Monitoring', icon: Activity, adminOnly: true },
  ]

  const visibleNavItems = navItems.filter(item => {
    if (item.adminOnly) return user?.is_admin === true
    return true
  })

  return (
    <aside className={clsx(
      'fixed left-0 top-0 h-screen layout-sidebar flex flex-col transition-all duration-300',
      isCollapsed ? 'w-20' : 'w-64'
    )}>
      {/* Logo */}
      <div className="p-6 border-b layout-sidebar-divider flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3 group">
          <img
            src="/images/repliqease_logo.png"
            alt="Repliqease Logo"
            className="w-14 h-14 rq-logo-frame shadow-lg transform group-hover:scale-105 transition-transform p-1.5"
          />
          {!isCollapsed && <span className="text-xl font-bold text-[var(--rq-text)]">Repliqease</span>}
        </Link>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-20 sidebar-toggle rounded-full p-1.5 shadow-lg transition-all duration-300 z-50"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
        {visibleNavItems.map(item => {
          const Icon = item.icon
          const isChildActive = item.children?.some(
            child => location.pathname === child.path || location.pathname.startsWith(child.path + '/')
          ) ?? false
          const isActive = !item.disabled && (
            isChildActive ||
            location.pathname === item.path ||
            (!item.children && location.pathname.startsWith(item.path + '/'))
          )

          if (item.disabled) {
            return (
              <div
                key={item.path}
                className={clsx(
                  'flex items-center px-4 py-3 rounded-xl text-sm font-medium cursor-not-allowed opacity-40 text-[var(--rq-text-soft)]',
                  isCollapsed ? 'justify-center' : 'space-x-3',
                )}
                title={isCollapsed ? `${item.label} (coming soon)` : 'Coming soon'}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </div>
            )
          }

          return (
            <div key={item.path}>
              <Link
                to={item.children ? item.children[0].path : item.path}
                className={clsx(
                  'sidebar-link',
                  isCollapsed ? 'justify-center' : 'space-x-3',
                  isActive
                    ? item.children ? 'sidebar-link-active' : 'sidebar-link-active-primary'
                    : ''
                )}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </Link>

              {/* Sub-items */}
              {item.children && isActive && !isCollapsed && (
                <div className="ml-4 mt-1 space-y-0.5">
                  {item.children.map(child => {
                    const ChildIcon = child.icon
                    const isChildActive = location.pathname === child.path
                    return (
                      <Link
                        key={child.path}
                        to={child.path + location.search}
                        className={clsx(
                          'sidebar-sub-link',
                          isChildActive
                            ? 'sidebar-sub-link-active'
                            : ''
                        )}
                      >
                        <ChildIcon className="w-4 h-4 flex-shrink-0" />
                        <span>{child.label}</span>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}

      </nav>

      {/* User profile */}
      {user && (
        <div className="border-t layout-sidebar-divider p-4">
          {!isCollapsed ? (
            <>
              <div className="flex items-center space-x-3 px-3 py-3 mb-2 rounded-xl sidebar-profile">
                <div className="w-9 h-9 bg-gradient-to-br from-[#33405a] to-[#222a3b] rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-[var(--rq-text)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--rq-text)] truncate">{user.email}</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-xl transition-all text-[var(--rq-text-soft)] hover:bg-white/5 hover:text-[var(--rq-text)]"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-2">
                <div className="w-9 h-9 bg-gradient-to-br from-[#33405a] to-[#222a3b] rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-[var(--rq-text)]" />
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium rounded-xl transition-all text-[var(--rq-text-soft)] hover:bg-white/5 hover:text-[var(--rq-text)]"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      )}
    </aside>
  )
}

export default Sidebar
